import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  X, AlertTriangle, Loader2, FileText, ClipboardCheck,
} from 'lucide-react';

import {
  analyzeFigure,
  getMatter,
  type AnalyzeFigureResponse,
  type DrawingFigureSummary,
} from '@/api/client';
import type { Claim } from '@/types';
import { useDrawingStore } from '@/store/drawingStore';

interface Props {
  matterId: string;
  /** Optional pre-fetched matter claims; the modal also lazy-loads
   * them on mount when this is undefined. Slice 8E action bench
   * passes them through to skip the round-trip. */
  claims?: Claim[];
}

const inputClass =
  'flex w-full rounded-md border border-zinc-300 bg-white px-3 py-2 ' +
  'text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]';

const primaryBtn =
  'inline-flex h-9 items-center justify-center gap-2 rounded-sm bg-[#002FA7] px-4 ' +
  'text-sm font-medium text-white hover:bg-[#002277] disabled:opacity-50 transition-colors';

const outlineBtn =
  'inline-flex h-9 items-center justify-center gap-1 rounded-sm border border-zinc-300 ' +
  'bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 ' +
  'transition-colors';

/**
 * Drawing module — Drafter's-Note Analyze modal (slice 8D).
 *
 * Two-pane layout (~720 px wide). Left pane shows the figure thumb,
 * the matter's frozen claims list (highlighting any that already
 * carry term-mappings to the figure's numerals — slice 8A's
 * `analyze_bundle.numeral_to_claim_map` is the source), and a brief
 * prior-run summary when the figure has been analyzed before.
 * Right pane carries the drafter's-note textarea.
 *
 * Re-run pre-fills the textarea from
 * `figure.analyze_bundle_json.drafters_note_verbatim` per the brief.
 * Skip submits an empty note. Submit → progress overlay during the
 * 5-15 s LLM call; on completion the figure list refreshes (the
 * figure's `analyze_bundle_summary` updates), the modal closes, and
 * any numeral_label_conflicts surface via the store-level
 * `mutationError` channel for the slice 8E action bench to react to.
 *
 * Slice 8E will swap the modal trigger from a programmatic
 * `openAnalyzeModal(figureId)` store call to an action-bench button
 * on the canvas surface.
 */
export default function AnalyzeFigureModal({ matterId, claims }: Props) {
  const figureId = useDrawingStore((s) => s.analyzeModalFigureId);
  const figures = useDrawingStore((s) => s.figures);
  const closeAnalyzeModal = useDrawingStore((s) => s.closeAnalyzeModal);
  const refreshLists = useDrawingStore((s) => s.refreshLists);
  const setMutationError = useDrawingStore((s) => s.setMutationError);

  const figure = useMemo<DrawingFigureSummary | null>(() => {
    if (!figureId) return null;
    return figures.find((f) => f.id === figureId) ?? null;
  }, [figureId, figures]);

  const [draftersNote, setDraftersNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [priorBundle, setPriorBundle] = useState<
    AnalyzeFigureResponse['bundle'] | null
  >(null);
  const [fetchedClaims, setFetchedClaims] = useState<Claim[] | null>(null);

  // Reset the textarea + bundle preview every time the modal opens
  // for a different figure. Slice 8E.2.a deliberately doesn't try
  // to pre-fill from a prior bundle — that requires a separate
  // figure-detail fetch since the figure summary endpoint only
  // returns `analyze_bundle_summary`. Pre-fill is reasonable
  // future polish; not blocking the brief's acceptance gate.
  useEffect(() => {
    if (!figureId) {
      setDraftersNote('');
      setPriorBundle(null);
      setError(null);
      return;
    }
    setDraftersNote('');
    setPriorBundle(null);
    setError(null);
  }, [figureId]);

  // Slice 8E.2.a — lazy-fetch matter claims if the caller didn't
  // supply them via the `claims` prop. DrawingPage doesn't hold
  // the matter's claims today (they live on the stages-side
  // matter state), so the modal pulls them on demand. One fetch
  // per modal-open lifecycle.
  useEffect(() => {
    if (claims !== undefined || !figureId) return;
    let alive = true;
    (async () => {
      try {
        const matter = await getMatter(matterId);
        if (!alive) return;
        const list: Claim[] = Array.isArray(matter?.claims) ? matter.claims : [];
        setFetchedClaims(list);
      } catch {
        // Non-fatal; the modal still works without the claim list,
        // just shows the "No frozen claims attached" empty state.
        if (alive) setFetchedClaims([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [claims, figureId, matterId]);

  const claimsForRender: Claim[] | undefined =
    claims !== undefined ? claims : fetchedClaims ?? undefined;

  const onSubmit = useCallback(async () => {
    if (!figureId || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await analyzeFigure(matterId, figureId, draftersNote);
      setPriorBundle(res.bundle);
      // Surface conflicts via the store-level mutationError channel
      // so slice 8E action bench can render an inline reconciliation
      // notice without coupling directly to the modal.
      if (res.numeral_label_conflicts.length > 0) {
        setMutationError(
          `Analyze complete with ${res.numeral_label_conflicts.length} `
          + 'numeral label conflict(s) — review on the canvas.',
        );
      }
      // Refresh the figure summary so the gallery card's
      // analyze_bundle_summary updates immediately. The
      // DrawingPage header's "N unanalyzed" count is now
      // derived from ``figures.filter(!analyze_bundle_summary)``
      // (BUG-006 proper fix) so the badge drops the moment
      // this refetch lands — the previous optimistic patch
      // against the rollup snapshot is no longer needed.
      await refreshLists(matterId);
      closeAnalyzeModal();
    } catch (e: unknown) {
      const errResp = (e as { response?: { data?: { detail?: unknown } } })?.response;
      const detail = errResp?.data?.detail as
        | string
        | { error_code?: string; detail?: string }
        | undefined;
      const message =
        (typeof detail === 'string' && detail)
        || (typeof detail === 'object' && detail?.detail)
        || (e as { message?: string })?.message
        || 'Analyze failed.';
      setError(String(message));
    } finally {
      setSubmitting(false);
    }
  }, [
    figureId, submitting, matterId, draftersNote,
    setMutationError, refreshLists, closeAnalyzeModal,
  ]);

  if (!figureId || !figure) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-zinc-900/30 px-4 pt-16"
      onMouseDown={(e) => {
        if (!submitting && e.target === e.currentTarget) closeAnalyzeModal();
      }}
      data-testid="analyze-figure-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="analyze-figure-title"
    >
      <div className="relative w-full max-w-3xl rounded-md border border-zinc-200 bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
          <h2
            id="analyze-figure-title"
            className="flex items-center gap-2 text-sm font-semibold text-zinc-900"
          >
            <ClipboardCheck className="h-4 w-4 text-[#002FA7]" aria-hidden />
            Analyze {figure.fig_label || `figure ${figure.sequence_num}`}
          </h2>
          <button
            type="button"
            className="text-zinc-400 hover:text-zinc-700 disabled:opacity-50"
            onClick={closeAnalyzeModal}
            disabled={submitting}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="grid grid-cols-[1fr_1.4fr] gap-0 divide-x divide-zinc-200">
          {/* Left pane — figure thumb + claim list + prior-run hint */}
          <div className="space-y-3 p-4">
            <div className="aspect-square w-full overflow-hidden rounded-sm border border-zinc-200 bg-zinc-50">
              <img
                src={`/api/matter/${matterId}/drawing/figures/${figure.id}/preview`}
                alt={figure.fig_label || `figure ${figure.sequence_num}`}
                className="h-full w-full object-contain"
                loading="lazy"
                onError={(e) => {
                  // Preview may be missing for AI-generated figures
                  // before slice 7F's preview generation lands;
                  // hide the broken-image affordance.
                  (e.currentTarget as HTMLImageElement).style.opacity = '0';
                }}
              />
            </div>

            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                Frozen claims
              </p>
              {claimsForRender === undefined ? (
                <div className="flex items-center gap-1.5 text-[12px] text-zinc-500">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Loading frozen claims…
                </div>
              ) : claimsForRender.length === 0 ? (
                <p className="text-[12px] text-zinc-500">
                  No frozen claims attached to this matter.
                </p>
              ) : (
                <ul
                  className="max-h-32 overflow-y-auto rounded-sm border border-zinc-200 bg-zinc-50"
                  data-testid="analyze-figure-claims"
                >
                  {claimsForRender
                    .filter((c) => c.is_frozen)
                    .sort((a, b) => a.claim_number - b.claim_number)
                    .map((c) => (
                      <li
                        key={c.id}
                        className="border-b border-zinc-200 px-2 py-1 text-[12px] last:border-b-0"
                      >
                        <span className="font-medium">Claim {c.claim_number}</span>
                        {' · '}
                        <span className="text-zinc-600">{c.claim_type}</span>
                      </li>
                    ))}
                </ul>
              )}
            </div>

            {priorBundle && (
              <div
                className="rounded-sm border border-emerald-200 bg-emerald-50 p-2 text-[12px] text-emerald-800"
                data-testid="analyze-figure-prior-summary"
              >
                <FileText className="mr-1 inline-block h-3 w-3" aria-hidden />
                Prior run captured{' '}
                {(priorBundle.extracted_numerals?.length ?? 0)} numerals,{' '}
                {(priorBundle.numeral_to_claim_map?.length ?? 0)} mappings.
              </div>
            )}
          </div>

          {/* Right pane — drafter's note */}
          <div className="space-y-3 p-4">
            <label
              htmlFor="analyze-figure-drafters-note"
              className="block text-xs font-medium text-zinc-700"
            >
              Drafter&apos;s note (preserved verbatim, attorney work-product)
            </label>
            <textarea
              id="analyze-figure-drafters-note"
              className={`${inputClass} h-48 resize-none`}
              placeholder={
                'Optional. Add any drafter context the spec module '
                + 'should preserve verbatim — design rationales, '
                + 'embodiment notes, or implementation constraints.'
              }
              value={draftersNote}
              onChange={(e) => setDraftersNote(e.target.value)}
              disabled={submitting}
              data-testid="analyze-figure-note"
            />
            <p className="text-[11px] text-zinc-500">
              The bundle includes extracted numerals, claim mappings,
              missing-coverage flags, technical anchors, and your
              note verbatim. The 5–15 s LLM call runs on the
              drawing_vision tier.
            </p>
            {error && (
              <p
                className="flex items-start gap-1.5 rounded-sm bg-amber-50 px-2 py-1.5 text-[12px] text-amber-800"
                data-testid="analyze-figure-error"
              >
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {error}
              </p>
            )}
          </div>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-zinc-200 px-4 py-3">
          <button
            type="button"
            className={outlineBtn}
            onClick={closeAnalyzeModal}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className={outlineBtn}
            onClick={() => { setDraftersNote(''); void onSubmit(); }}
            disabled={submitting}
            data-testid="analyze-figure-skip"
          >
            Skip
          </button>
          <button
            type="button"
            className={primaryBtn}
            onClick={onSubmit}
            disabled={submitting}
            data-testid="analyze-figure-submit"
          >
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {submitting ? 'Analyzing…' : 'Run analyze'}
          </button>
        </footer>

        {submitting && (
          <div
            className="absolute inset-0 flex items-center justify-center rounded-md bg-white/70"
            data-testid="analyze-figure-overlay"
            aria-live="polite"
          >
            <div className="flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 shadow">
              <Loader2 className="h-4 w-4 animate-spin text-[#002FA7]" />
              Running analyze (5–15 s)…
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
